/**
 * RolesPage Component
 *
 * Main page for managing global roles with search and CRUD operations.
 * Roles are tenant-wide and can be assigned to users, groups, or applications.
 *
 * Role Type Assignment Rules:
 * - TENANT roles: Can be assigned to users and groups
 * - APPLICATION roles: Can be assigned to applications only
 * - APP_DOMAIN roles: Can be assigned to users and groups
 */

import React, { useState, useMemo } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { DashboardLayout } from '../../../components/layout/DashboardLayout';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { RoleTable } from '../components/RoleTable';
import { CreateRoleModal } from '../components/CreateRoleModal';
import { RoleDetailsModal } from '../components/RoleDetailsModal';
import { UpdateRoleModal } from '../components/UpdateRoleModal';
import { AssignRoleModal } from '../components/AssignRoleModal';
import { DeleteRoleModal } from '../components/DeleteRoleModal';
import { CopyRoleModal } from '../components/CopyRoleModal';
import { useRoles } from '../hooks/useRoles';
import { useAuth } from '../../../contexts/AuthContext';
import { hasPermission } from '../../../lib/utils/permissions';
import { useLayoutProps } from '../../../hooks/useLayoutProps';
import type { RoleSanitized } from '../../../types/role.types';
import { getErrorMessage } from '../../../lib/utils/errors';

export const RolesPage: React.FC = () => {
  const { tenant, activeRole } = useAuth();
  const layoutProps = useLayoutProps();

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [selectedRole, setSelectedRole] = useState<RoleSanitized | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
  const [showAssignModal, setShowAssignModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showCopyModal, setShowCopyModal] = useState<boolean>(false);

  const tenantId = tenant?.tenantId || '';

  // Permission checks
  const canCreate = hasPermission(activeRole?.permissions, 'iam:role:create');
  const canUpdate = hasPermission(activeRole?.permissions, 'iam:role:update');
  const canDelete = hasPermission(activeRole?.permissions, 'iam:role:delete');

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch all roles
  const {
    data: roles = [],
    isLoading: isLoadingRoles,
    isFetching,
    error,
    refetch,
  } = useRoles(tenantId);

  // Filter roles by search query
  const filteredRoles = useMemo(() => {
    if (!roles) return [];

    let filtered = [...roles];

    // Search filter
    if (debouncedSearch) {
      const query = debouncedSearch.toLowerCase();
      filtered = filtered.filter(
        (role) =>
          role.roleName.toLowerCase().includes(query) ||
          (role.description && role.description.toLowerCase().includes(query)) ||
          (role.path && role.path.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [roles, debouncedSearch]);

  const handleViewDetails = (role: RoleSanitized) => {
    setSelectedRole(role);
    setShowDetailsModal(true);
  };

  const handleEdit = (role: RoleSanitized) => {
    setSelectedRole(role);
    setShowUpdateModal(true);
  };

  const handleAssign = (role: RoleSanitized) => {
    setSelectedRole(role);
    setShowAssignModal(true);
  };

  const handleDelete = (role: RoleSanitized) => {
    setSelectedRole(role);
    setShowDeleteModal(true);
  };

  const handleCopy = (role: RoleSanitized) => {
    setSelectedRole(role);
    setShowCopyModal(true);
  };

  return (
    <DashboardLayout {...layoutProps}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Roles</h1>
            <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Define global roles with permissions and assign them to users, groups, or applications
            </p>
          </div>
          {canCreate && (
            <Button onClick={() => setShowCreateModal(true)}>
              Create Role
            </Button>
          )}
        </div>

        {/* Search */}
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Search roles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <ArrowPathIcon className={`h-4 w-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="error">
            {getErrorMessage(error)}
          </Alert>
        )}

        {/* Roles Table */}
        <RoleTable
          roles={filteredRoles}
          isLoading={isLoadingRoles}
          onViewRole={handleViewDetails}
          onEditRole={handleEdit}
          onDeleteRole={handleDelete}
          onAssignRole={handleAssign}
          onCopyRole={handleCopy}
          canEdit={canUpdate}
          canCreate={canCreate}
          canDelete={canDelete}
        />

        {/* Modals */}
        <CreateRoleModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => refetch()}
        />

        <RoleDetailsModal
          role={selectedRole}
          tenantId={tenantId}
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedRole(null);
          }}
        />

        <UpdateRoleModal
          role={selectedRole}
          tenantId={tenantId}
          isOpen={showUpdateModal}
          onClose={() => {
            setShowUpdateModal(false);
            setSelectedRole(null);
          }}
          onSuccess={() => refetch()}
        />

        <AssignRoleModal
          role={selectedRole}
          tenantId={tenantId}
          isOpen={showAssignModal}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedRole(null);
          }}
        />

        {selectedRole && (
          <CopyRoleModal
            isOpen={showCopyModal}
            onClose={() => {
              setShowCopyModal(false);
              setSelectedRole(null);
            }}
            onSuccess={() => refetch()}
            sourceRole={selectedRole}
          />
        )}

        {selectedRole && (
          <DeleteRoleModal
            isOpen={showDeleteModal}
            onClose={() => {
              setShowDeleteModal(false);
              setSelectedRole(null);
            }}
            role={selectedRole}
            onSuccess={() => refetch()}
          />
        )}
      </div>
    </DashboardLayout>
  );
};
